"use client";

import Link from "next/link";
import { useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "framer-motion";
import { Languages, Moon, Sun, ChevronDown, Menu, X } from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { toggleDarkMode } from "@/store/slices/darkModeSlice";
import { setLanguage } from "@/store/slices/languageSlice";
import { useTranslation } from "react-i18next";

type LangCode = "en" | "de";

export default function Navbar({ lang }: { lang: LangCode }) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation();

  const isDark = useAppSelector((state) => state.darkMode?.enabled ?? false);
  const currentLang = useAppSelector((state) => state.language.current);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 180], [0, -8]);
  const shadowOpacity = useTransform(scrollY, [0, 220], [0.08, 0.28]);
  const boxShadow = useMotionTemplate`0 8px 24px rgba(0,0,0,${shadowOpacity})`;

  const languages: { code: LangCode; label: string }[] = [
    { code: "en", label: "English" },
    { code: "de", label: "Deutsch" },
  ];

  const navItems = [
    { href: `/${lang}`, label: t("nav_home", "Home") },
    { href: `/${lang}/projects`, label: t("nav_background", "Background") },
  ];

  const displayedLangCode: LangCode = currentLang === "de" ? "de" : "en";
  const displayedLangLabel =
    languages.find((item) => item.code === displayedLangCode)?.label ??
    "English";

  const handleChangeLanguage = (selectedLang: LangCode) => {
    dispatch(setLanguage(selectedLang));

    const segments = pathname.split("/");
    segments[1] = selectedLang;
    const nextPath = segments.join("/") || `/${selectedLang}`;

    router.push(nextPath);
    setIsLanguageOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <motion.header style={{ y }} className="sticky top-0 z-50">
      <motion.div
        style={{ boxShadow }}
        className="mx-auto flex max-w-7xl items-center justify-between rounded-b-xl border-x border-b border-black/20 bg-white px-4 py-3 dark:border-white/25 dark:bg-neutral-950"
      >
        <Link
          href={`/${lang}`}
          className="font-display px-2 py-1 text-lg font-semibold tracking-[-0.02em]"
        >
          MOWA
        </Link>

        <div className="hidden items-center gap-2 md:flex">
          {navItems.map((item) => {
            const isActive = pathname === item.href;

            return (
              <Link
                key={item.href}
                href={item.href}
                className={`font-display rounded-lg border border-transparent px-4 py-2 text-sm uppercase tracking-[0.08em] transition-all ${
                  isActive
                    ? "border-green-600 text-green-600"
                    : "text-neutral-700 hover:border-black/20 dark:text-neutral-200 dark:hover:border-white/30"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <div className="relative">
            <button
              onClick={() => setIsLanguageOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-lg border border-black/20 px-3 py-2 text-sm hover:bg-black/5 dark:border-white/25 dark:hover:bg-white/10"
            >
              <Languages size={14} />
              <span>{displayedLangLabel}</span>
              <ChevronDown size={14} />
            </button>

            <AnimatePresence>
              {isLanguageOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  className="glass-panel absolute right-0 top-12 z-20 min-w-36 p-1"
                >
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => handleChangeLanguage(language.code)}
                  className="block w-full rounded-lg border border-transparent px-3 py-2 text-left text-sm hover:border-black/20 hover:bg-black/5 dark:hover:border-white/30 dark:hover:bg-white/10"
                    >
                      {language.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => dispatch(toggleDarkMode())}
            className="rounded-lg border border-black/20 p-2 hover:bg-black/5 dark:border-white/25 dark:hover:bg-white/10"
            aria-label="Toggle color mode"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        <button
          className="rounded-lg border border-black/20 p-2 md:hidden dark:border-white/25"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </motion.div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="glass-panel mx-auto mt-2 max-w-7xl rounded-xl border-x border-b border-black/20 p-3 md:hidden dark:border-white/25"
          >
            <div className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="block rounded-lg border border-transparent px-3 py-2 text-sm uppercase tracking-[0.08em] hover:border-black/20 hover:bg-black/5 dark:hover:border-white/30 dark:hover:bg-white/10"
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleChangeLanguage(language.code)}
                  className="rounded-lg border border-black/20 px-3 py-2 text-sm dark:border-white/25"
                >
                  {language.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => dispatch(toggleDarkMode())}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-black/20 px-3 py-2 text-sm dark:border-white/25"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
              {isDark
                ? t("common.lightMode", "Light Mode")
                : t("common.darkMode", "Dark Mode")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
